// @flow

type ConstantMapping = {
  [name: string]: string,
};
type ColorsMapping = {
  [name: string]: string | ConstantMapping,
};
const colors = {
  // Neutrals
  white: '#ffffff',
  lightestGrey: '#fafafa',
  lighterGrey: '#f3f3f3',
  lightGrey: '#e1e1e1',
  mediumLightGrey: '#c8c8c8',
  mediumGrey: '#909090',
  darkGrey: '#666666',
  darkerGrey: '#424242',
  darkestGrey: '#222222',

  // Reds
  veryLightRed: '#fff4f5',
  almostVeryLightRed: '#fff2f2', // only used in seedling
  lightRed: '#f8d9de',
  darkRed: '#d8372a',

  // Yellows
  veryLightYellow: '#fefcdd',
  lightYellow: '#fbf38d',
  darkYellow: '#f2cd00',
  darkestYellow: '#a99000',
  canaryYellow: '#fffbe5',

  // Greens
  lighterGreenhouseGreen: '#e6fff8',
  lightGreenhouseGreen: '#ccf0ee',
  mediumGreenhouseGreen: '#008561',
  darkGreenhouseGreen: '#24a47f',
  budgieGreen: '#f3fffb',
  evergreen: '#15372c',

  // Blues
  lightBlue: '#d6e6ff',
  mediumBlue: '#3574d6',
  darkBlue: '#054ab2',

  babyBlue: '#edf3fa',
  toddlerBlue: '#dbe6f6',

  // Oranges
  lightOrange: '#ffd9c7',
  orange: '#ff6519',
  darkOrange: '#d44a00',

  poppy: '#F7C8B0',
  iris: '#C3D5F5',
  lavender: '#E0DCEC',

  // GHO
  ghoBlue: '#4a9cd5',
};
(colors: ColorsMapping);

type Color = number;

type RGB = [Color, Color, Color];

/**
 * Convert '#123' to '#112233'
 */
const convertShortFormHexToLongFormHex = (hex: string): string =>
  hex.replace(/[a-f\d]/gi, '$&$&');

const hexToRGB = (hex: string): ?RGB => {
  const shortFormLength = 4;
  const longFormLength = 7;

  if (![shortFormLength, longFormLength].includes(hex.length)) {
    return null;
  }

  if (hex.length === shortFormLength) {
    hex = convertShortFormHexToLongFormHex(hex);
  }

  const rgb = [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];

  if (rgb.includes(NaN)) {
    return null;
  }

  return rgb;
};

export const withOpacity = (color: $Values<typeof colors>, opacity: number) => {
  const convertedColor = hexToRGB(color);

  if (!convertedColor) {
    throw Error(`Color '${color}' cannot be converted to RGB`);
  }

  const [r, g, b] = convertedColor;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const padding = {
  tiny: '5px',
  small: '10px',
  normal: '20px',
  large: '30px',
};
(padding: ConstantMapping);

export const fontSize = {
  small: '0.9rem',
  normal: '1rem',
  large: '1.1rem',
  veryLarge: '1.4rem',
  huge: '1.7rem',
};
(fontSize: ConstantMapping);

export type FontWeight = 'light' | 'normal' | 'heavy' | 'heaviest';

export const fontWeight = (amount: FontWeight): string =>
  `font-weight: ${fontWeight[amount]};`;

fontWeight.light = 300;
fontWeight.normal = 400;
fontWeight.heavy = 600;
fontWeight.heaviest = 700;

export const themes = ['gho', 'ghr'];

export const DraggableItemTypes = {
  GENERIC_ROW: 'GENERIC_ROW',
};

export { colors };
